// @ts-check
/**
 * 🔐 CANONICAL AUTHENTICATION MODULE
 *
 * Single source of truth for all authentication operations.
 * Eliminates duplication and provides consistent CORS handling.
 */

import { env } from '../../config/env';

/**
 * Internal helper to build API URLs using the validated environment config.
 * This avoids circular dependencies with utils/api.js
 * @param {string} path - The API endpoint path
 * @returns {string} The full API URL
 */
function buildApiUrl(path) {
  const base = env.backendUrl || '';
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${base}${normalizedPath}`;
}

// Platform constants for getCurrentUser
const PLATFORM = {
  FARCASTER: 'farcaster',
  BASE_MINIKIT: 'baseMiniKit',
  WORLDCOIN: 'worldcoin'
};

const IDENTITY_TYPE = {
  EOA: 'eoa',
  FARCASTER: 'farcaster',
  BASE_MINIKIT: 'baseMiniKit',
  WORLDCOIN: 'worldcoin'
};

// ============================================================================
// CONFIGURATION
// ============================================================================

const AUTH_CONFIG = {
  CACHE_TTL: 5 * 60 * 1000, // 5 minutes
  REQUEST_TIMEOUT: 10000,   // 10 seconds
  MAX_RETRIES: 2,
  ENDPOINTS: {
    NONCE: '/auth/nonce',
    SIGNIN: '/auth/signin',
    ME: '/auth/me/api',  // Reverted back to original endpoint
    LOGOUT: '/auth/logout'
  }
};

// ============================================================================
// CACHE MANAGEMENT
// ============================================================================

let authCache = {
  result: null,
  wallet: '',
  timestamp: 0,
  ttl: AUTH_CONFIG.CACHE_TTL
};

function isCacheValid(wallet) {
  const now = Date.now();
  return authCache.result !== null &&
         authCache.wallet === wallet &&
         (now - authCache.timestamp) < authCache.ttl;
}

function updateCache(wallet, result) {
  authCache = {
    result,
    wallet,
    timestamp: Date.now(),
    ttl: AUTH_CONFIG.CACHE_TTL
  };
}

function clearCache() {
  authCache = { result: null, wallet: '', timestamp: 0, ttl: AUTH_CONFIG.CACHE_TTL };
}

// ============================================================================
// HTTP UTILITIES
// ============================================================================

async function fetchWithTimeout(url, options = {}, timeout = AUTH_CONFIG.REQUEST_TIMEOUT) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      // CANONICAL CORS CONFIGURATION - NO HARDCODED HEADERS
      mode: 'cors',
      credentials: 'include',
      cache: 'no-store'
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error(`Request timeout after ${timeout}ms`, { cause: error });
    }
    throw error;
  }
}

// ============================================================================
// TOKEN MANAGEMENT (Centralized)
// ============================================================================

export function getToken() {
  if (typeof window === 'undefined') return null;
  // The long-lived backend API key is the primary bearer credential. The
  // short-lived access JWT is only a fallback; preferring it caused sessions
  // to expire and force a new wallet signature while a valid API key existed.
  return (
    localStorage.getItem('dcw_api_key') ||
    localStorage.getItem('dcw_token') ||
    localStorage.getItem('apiKey')
  );
}

export function setAccessToken(token) {
  if (typeof window === 'undefined') return;
  if (token) localStorage.setItem('dcw_access_token', token);
  else localStorage.removeItem('dcw_access_token');
}

export function setToken(token) {
  if (typeof window === 'undefined') return;
  if (token) {
    localStorage.setItem('dcw_token', token);
    localStorage.setItem('dcw_api_key', token); // Compat
  } else {
    localStorage.removeItem('dcw_token');
    localStorage.removeItem('dcw_api_key');
    localStorage.removeItem('apiKey');
  }
}

export function getAuthWallet() {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem('dcw_wallet') || localStorage.getItem('dcw_auth_wallet');
}

export function setAuthWallet(wallet) {
  if (typeof window === 'undefined') return;
  if (wallet) {
    const normalizedWallet = wallet.toLowerCase();
    localStorage.setItem('dcw_wallet', normalizedWallet);
    localStorage.setItem('dcw_auth_wallet', normalizedWallet); // Compat
  } else {
    localStorage.removeItem('dcw_wallet');
    localStorage.removeItem('dcw_auth_wallet');
  }
}

export function clearSession() {
  setToken(null);
  setAuthWallet(null);
  localStorage.removeItem('dcw_access_token');
  localStorage.removeItem('dcw_refresh_token');
  localStorage.removeItem('dcw_chat_api_key');
  localStorage.removeItem('dcw_siwe_completed');
  clearCache();
  console.log('[Auth] Session cleared');
}

// ============================================================================
// AUTHENTICATION METHODS
// ============================================================================

/**
 * Get authentication nonce for wallet signing
 */
export async function getNonce(walletAddress) {
  if (!walletAddress) {
    throw new Error('Wallet address is required');
  }

  const normalizedAddress = walletAddress.toLowerCase();
  const url = buildApiUrl(AUTH_CONFIG.ENDPOINTS.NONCE) + `?address=${normalizedAddress}`;

  console.log('[Auth] Fetching nonce for:', normalizedAddress);

  for (let attempt = 1; attempt <= AUTH_CONFIG.MAX_RETRIES; attempt++) {
    try {
      console.log(`[Auth] Attempt ${attempt}/${AUTH_CONFIG.MAX_RETRIES} - GET ${url}`);

      const response = await fetchWithTimeout(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('[Auth] Nonce received successfully');
      return data;

    } catch (error) {
      console.error(`[Auth] Attempt ${attempt} failed:`, error.message);
      if (attempt === AUTH_CONFIG.MAX_RETRIES) {
        throw error;
      }
      await new Promise(resolve => setTimeout(resolve, AUTH_CONFIG.RETRY_DELAY * attempt));
    }

  }
}

/**
 * Sign in with wallet signature
 */
export async function signIn(signature, message, address, extraData = {}) {
  if (!address) {
    throw new Error('Wallet address required');
  }

  const authType = extraData.authType || 'signature';
  const isContextAuth = authType === 'context' || !signature || signature === 'context-auth';

  if (isContextAuth) {
    // Context-only payloads carry no verifiable proof. Platforms must exchange
    // a signed provider token through /auth/exchange instead.
    throw new Error(
      'Context-only authentication is disabled. Provide a wallet signature or use provider token exchange.'
    );
  }

  const url = buildApiUrl(AUTH_CONFIG.ENDPOINTS.SIGNIN);
  const response = await fetchWithTimeout(url, {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      address,
      signature,
      message: message || null,
      nonce: extraData.nonce || null,
      authType,
      platform: extraData.platform || 'web',
      fid: extraData.fid || null,
      username: extraData.username || null,
    }),
  });

  if (!response.ok) {
    let detail = `Authentication failed (${response.status})`;
    try {
      const body = await response.json();
      detail = body.detail || body.message || detail;
    } catch {
      // keep default detail
    }
    throw new Error(detail);
  }

  const data = await response.json();
  const apiKey = data.api_key || null;
  const accessToken = data.access_token || null;
  const primaryToken = apiKey || accessToken;
  if (!primaryToken) {
    throw new Error('Authentication failed: backend returned no token');
  }

  // Persist the 90-day API key as the primary bearer. Keep the access JWT
  // separately for endpoints that require JWT claims; never overwrite the
  // API key with a short-lived access token.
  setToken(primaryToken);
  setAuthWallet(address);
  setAccessToken(accessToken);
  if (data.refresh_token) localStorage.setItem('dcw_refresh_token', data.refresh_token);

  return {
    ...data,
    api_key: apiKey || primaryToken,
    access_token: accessToken || primaryToken,
  };
}

/**
 * Exchange a signed platform token (Farcaster Quick Auth, Base, World) for a
 * backend-issued credential bound to the verified wallet.
 * @param {string} provider - quick | frame | base | world
 * @param {string} jwt - provider-issued token
 * @returns {Promise<Object>} backend auth payload
 */
export async function exchangeProviderToken(provider, jwt) {
  if (!provider || !jwt) {
    throw new Error('Provider token exchange requires provider and token');
  }

  const url = buildApiUrl('/auth/exchange');
  const response = await fetchWithTimeout(url, {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ provider, jwt }),
  });

  if (!response.ok) {
    let detail = `Provider exchange failed (${response.status})`;
    try {
      const body = await response.json();
      detail = body.detail || body.message || detail;
    } catch {
      // keep default detail
    }
    throw new Error(detail);
  }

  const data = await response.json();
  const token = data.api_key || data.access_token;
  if (!token) {
    throw new Error('Provider exchange returned no credential');
  }

  setToken(token);
  if (data.wallet) setAuthWallet(data.wallet);

  return {
    ...data,
    api_key: data.api_key || token,
    access_token: data.access_token || token,
  };
}

/**
 * Check if user is authenticated
 */
export async function isAuthenticated(walletAddress) {
  if (!walletAddress) {
    console.log('[Auth] No wallet address provided');
    return false;
  }

  // Check cache first
  if (isCacheValid(walletAddress)) {
    return authCache.result;
  }

  // Use a simple health check first to verify server connectivity
  const healthUrl = buildApiUrl('/health');

  try {
    // First check if server is reachable
    const healthResponse = await fetchWithTimeout(healthUrl, {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    }, 3000);

    if (!healthResponse.ok) {
      updateCache(walletAddress, false);
      return false;
    }

    // If we have a cached result and server is reachable, use cache
    if (isCacheValid(walletAddress)) {
      return authCache.result;
    }

    // Now do the actual auth check
    const url = buildApiUrl(AUTH_CONFIG.ENDPOINTS.ME);

    const apiKey = getToken();
    console.log(`[Auth] 🔍 DEBUGGING: isAuthenticated for wallet ${walletAddress}:`, {
      hasApiKey: !!apiKey,
      apiKeyLength: apiKey?.length,
      apiKeyPrefix: apiKey?.substring(0, 10) + '...',
      url: url,
      localStorage_dcw_api_key: localStorage.getItem('dcw_api_key') ? 'EXISTS' : 'NULL',
      localStorage_apiKey: localStorage.getItem('apiKey') ? 'EXISTS' : 'NULL'
    });
    
    const headers = {
      'Accept': 'application/json'
    };

    // API keys are accepted by every protected route and live for 90 days.
    // Only use the short-lived access JWT when no API key is stored.
    const authToken =
      localStorage.getItem('dcw_api_key') ||
      localStorage.getItem('dcw_token') ||
      localStorage.getItem('dcw_access_token') ||
      apiKey;
    
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    const response = await fetchWithTimeout(url, {
      method: 'GET',  // Use GET instead of HEAD to avoid CORS preflight issues
      headers: headers
    }, 5000); // Shorter timeout for auth checks

    const isValid = response.ok;
    console.log(`[Auth] 🔍 DEBUGGING: isAuthenticated response:`, {
      status: response.status,
      statusText: response.statusText,
      isValid: isValid,
      headers: response.headers ? Object.fromEntries(response.headers.entries()) : 'N/A'
    });
    
    if (!isValid) {
      try {
        const errorText = await response.text();
        console.log(`[Auth] 🔍 DEBUGGING: Error response body:`, errorText);
      } catch (e) {
        console.log(`[Auth] 🔍 DEBUGGING: Could not read error response body`);
      }
    }
    
    updateCache(walletAddress, isValid);

    return isValid;
  } catch (error) {
    console.error('[Auth] Authentication check failed:', error.message);
    updateCache(walletAddress, false);
    return false;
  }
}

/**
 * Sign out user
 */
export async function signOut() {
  const url = buildApiUrl(AUTH_CONFIG.ENDPOINTS.LOGOUT);

  try {
    console.log('[Auth] Signing out');

    await fetchWithTimeout(url, {
      method: 'POST',
      headers: {
        'Accept': 'application/json'
      }
    });

    clearCache();
    clearSession();
    console.log('[Auth] Sign out successful');
  } catch (error) {
    console.error('[Auth] Sign out failed:', error.message);
    // Clear local state even when the server is unreachable.
    clearSession();
  }
}

// ============================================================================
// USER INFO FUNCTIONS
// ============================================================================

/**
 * Get current user information
 *
 * @returns {Promise<Object|null>} - User information or null if not authenticated
 */
export async function getCurrentUser() {
  try {
    // Get wallet address from localStorage first
    const walletAddress = localStorage.getItem('dcw_wallet');
    if (!walletAddress) {
      return null;
    }

    // Check if user is authenticated with the wallet address
    const isAuth = await isAuthenticated(walletAddress);
    if (!isAuth) {
      return null;
    }

    // Basic user info available on all platforms
    const user = {
      address: walletAddress,
      platform: 'desktop', // Default platform
      type: IDENTITY_TYPE.EOA
    };

    // Add platform-specific user info
    if (typeof window !== 'undefined') {
      // Farcaster MiniKit
      if (window.miniKit?.context?.user) {
        user.fid = window.miniKit.context.user.fid;
        user.username = window.miniKit.context.user.username;
        user.displayName = window.miniKit.context.user.displayName;
        user.pfp = window.miniKit.context.user.pfp?.url;
        user.type = IDENTITY_TYPE.FARCASTER;
        user.platform = PLATFORM.FARCASTER;
      }
      // Base MiniKit
      else if (window.miniKit?.context) {
        user.custody = window.miniKit.context.custody;
        user.fid = window.miniKit.context.fid;
        user.type = IDENTITY_TYPE.BASE_MINIKIT;
        user.platform = PLATFORM.BASE_MINIKIT;
      }
      // WorldCoin
      else if (window.worldcoin) {
        try {
          const credential = await window.worldcoin.getCredential();
          if (credential) {
            user.worldId = credential.nullifier_hash;
            user.verified = credential.verified || false;
            user.type = IDENTITY_TYPE.WORLDCOIN;
            user.platform = PLATFORM.WORLDCOIN;
          }
        } catch (error) {
          console.error('[Auth] WorldCoin get credential failed:', error);
        }
      }
    }

    return user;
  } catch (error) {
    console.error('[Auth] Error getting current user:', error);
    return null;
  }
}

/**
 * Get current user information synchronously
 *
 * @returns {Object|null} - User information or null if not available
 */
export function getCurrentUserSync() {
  try {
    // Get wallet address from localStorage
    const walletAddress = localStorage.getItem('dcw_wallet');
    if (!walletAddress) {
      return null;
    }

    // Basic user info available on all platforms
    const user = {
      address: walletAddress,
      platform: 'desktop', // Default platform
      type: IDENTITY_TYPE.EOA
    };

    // Add platform-specific user info (synchronous only)
    if (typeof window !== 'undefined') {
      // CRITICAL FIX: For Farcaster, ALWAYS read from stored context first
      // The wallet address format 'farcaster:FID' is our source of truth
      if (walletAddress.startsWith('farcaster:')) {
        try {
          const storedContext = localStorage.getItem('dcw_platform_context');
          if (storedContext) {
            const context = JSON.parse(storedContext);
            if (context.fid && context.source === 'farcaster-miniapp') {
              user.fid = context.fid;
              user.username = context.username;
              user.displayName = context.displayName;
              user.type = IDENTITY_TYPE.FARCASTER;
              user.platform = PLATFORM.FARCASTER;
              console.log('[Auth] 🎯 Loaded Farcaster context from storage:', {
                fid: user.fid,
                username: user.username,
                displayName: user.displayName
              });
            } else {
              console.warn('[Auth] ⚠️ Stored context invalid or missing FID');
            }
          } else {
            console.warn('[Auth] ⚠️ No stored Farcaster context found');
          }
        } catch (e) {
          console.warn('[Auth] Failed to parse stored Farcaster context:', e);
        }
      }
      // Fallback: Try SDK context if wallet is not farcaster:FID format
      else if (window.miniKit?.context?.user) {
        user.fid = window.miniKit.context.user.fid;
        user.username = window.miniKit.context.user.username;
        user.displayName = window.miniKit.context.user.displayName;
        user.pfp = window.miniKit.context.user.pfp?.url;
        user.type = IDENTITY_TYPE.FARCASTER;
        user.platform = PLATFORM.FARCASTER;
      }
      // Base MiniKit
      else if (window.miniKit?.context) {
        user.custody = window.miniKit.context.custody;
        user.fid = window.miniKit.context.fid;
        user.type = IDENTITY_TYPE.BASE_MINIKIT;
        user.platform = PLATFORM.BASE_MINIKIT;
      }
    }

    return user;
  } catch (error) {
    console.error('[Auth] Error getting current user sync:', error);
    return null;
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

export { clearCache as invalidateAuthCache };
